import React, { useState, useEffect } from "react";
import MyLoader from "../MyLoader";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

const SubscriptionPlan = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customProperties, setCustomProperties] = useState({});

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://api.dev.theflexmanager.com/api/payment/getAll/package`,
          { method: "GET" }
        );
        const result = await response.json();
        if (result.status === "success") {
          setPackages(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch packages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const getFilteredPackages = () => {
    const isAnnual = billingCycle === "annual";
    return packages.filter((pkg) => pkg.isAnnual === isAnnual);
  };

  const mapPackageToDisplay = (apiPackage) => {
    return {
      _id: apiPackage._id,
      package: apiPackage.planName,
      totalPrice: `$${apiPackage.totalPackagePrice}`,
      monthlyPrice: `$${Math.round(apiPackage.monthlyPrice)}`,
      savePerYear: apiPackage.savePrice > 0 ? `$${apiPackage.savePrice}` : null,
      description: apiPackage.description,
      durationLabel: apiPackage.isAnnual ? "12-Month Total" : "6-Month Total",
      features: apiPackage.allowFeatures || [],
      allowProperties: apiPackage.allowProperties,
      isCustom: apiPackage.isCustom,
      pricePerProperty: apiPackage.pricePerProperty,
      numberOfMonth: apiPackage.numberOfMonth,
    };
  };

  const calculateCustomPrice = (apiPackage, propertyCount) => {
    if (!propertyCount || propertyCount <= 0) {
      return {
        totalPrice: "$0",
        monthlyPrice: "$0",
        discountPercentage: 0,
        discountAmount: "$0",
        originalPrice: "$0",
      };
    }

    const originalPrice = propertyCount * apiPackage.pricePerProperty;

    let discountPercentage = 0;
    if (propertyCount >= 25) {
      discountPercentage = 20;
    } else if (propertyCount >= 10) {
      discountPercentage = 10;
    }

    const discountAmount = Math.round(
      (originalPrice * discountPercentage) / 100
    );
    const totalPrice = originalPrice - discountAmount;
    const monthlyPrice = Math.round(totalPrice / apiPackage.numberOfMonth);

    return {
      totalPrice: `$${totalPrice.toLocaleString()}`,
      monthlyPrice: `$${monthlyPrice.toLocaleString()}`,
      discountPercentage,
      discountAmount: `$${discountAmount.toLocaleString()}`,
      originalPrice: `$${originalPrice.toLocaleString()}`,
    };
  };

  return (
    <div className="w-full flex items-start px-4 sm:px-6 lg:px-8">
      <div className="w-[95%] mx-auto pt-10 pb-20">
        {/* Header */}
        <div className="text-center pb-10">
          <p className="font-popinsMedium text-sm uppercase tracking-widest text-paraColor mb-3">
            Transparent Pricing
          </p>
          <ParagraphTag
            content="Choose the Plan That Fits Your Portfolio"
            classes="text-darkColor font-popinsBold text-2xl md:text-3xl lg:text-4xl leading-tight"
          />
          <p className="font-popinsRegular text-paraColor text-sm md:text-base mt-3 max-w-lg mx-auto leading-relaxed">
            No hidden fees. No lock-in surprises. Scale up anytime as your
            flex space portfolio grows.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-gray-100 rounded-xl p-1 flex gap-1">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2.5 text-sm font-popinsSemiBold rounded-lg transition-all duration-300 ${
                billingCycle === "monthly"
                  ? "bg-themeColor text-white shadow-sm"
                  : "text-darkColor hover:text-themeColor bg-transparent"
              }`}
            >
              6 Months
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-6 py-2.5 text-sm font-popinsSemiBold rounded-lg transition-all duration-300 ${
                billingCycle === "annual"
                  ? "bg-themeColor text-white shadow-sm"
                  : "text-darkColor hover:text-themeColor bg-transparent"
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-statusColor text-white rounded-full px-2 py-0.5">
                Save more
              </span>
            </button>
          </div>
        </div>

        {/* Subscription Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {loading ? (
            <div className="col-span-full flex justify-center items-center py-16">
              <MyLoader />
            </div>
          ) : (
            getFilteredPackages().map((apiPackage) => {
              const displayPackage = mapPackageToDisplay(apiPackage);
              const isCustom = displayPackage.isCustom;
              const customPropertyCount = customProperties[apiPackage._id] || 0;
              const customPricing = isCustom
                ? calculateCustomPrice(apiPackage, customPropertyCount)
                : null;

              return (
                <div
                  key={apiPackage._id}
                  className="w-full cursor-pointer rounded-2xl bg-white group hover:bg-themeColor transition-all duration-500 transform hover:-translate-y-1 hover:shadow-2xl border border-gray-200 flex flex-col h-full"
                >
                  <div className="flex-1 flex flex-col mt-7 w-[88%] mx-auto">
                    {/* Plan header */}
                    <div className="border-b border-darkColor/10 pb-6 group-hover:border-white/20">
                      <ParagraphTag
                        content={displayPackage.package}
                        classes="text-themeColor font-popinsBold text-lg group-hover:text-white transition-colors duration-300"
                      />
                      <ParagraphTag
                        content={displayPackage.durationLabel}
                        classes="text-paraColor font-popinsRegular text-xs mt-0.5 group-hover:text-white/60 transition-colors duration-300"
                      />

                      {/* Properties allowed */}
                      <div className="mt-3 inline-flex items-center bg-themeColor/10 group-hover:bg-white/10 rounded-full px-3 py-1 transition-colors duration-300">
                        <ParagraphTag
                          content={
                            isCustom
                              ? "Custom — Any Portfolio Size"
                              : `Up to ${displayPackage.allowProperties} properties`
                          }
                          classes="text-themeColor font-popinsMedium text-xs group-hover:text-white transition-colors duration-300"
                        />
                      </div>

                      {/* Custom property input */}
                      {isCustom && (
                        <div className="mt-4">
                          <label className="text-darkColor font-popinsMedium text-xs group-hover:text-white/80 transition-colors duration-300 block mb-1.5">
                            Number of Properties:
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={customPropertyCount || ""}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setCustomProperties((prev) => ({
                                ...prev,
                                [apiPackage._id]: value,
                              }));
                            }}
                            placeholder="Enter number of properties"
                            className="w-full text-sm font-popinsRegular bg-bgColor px-3 py-2.5 rounded-lg text-darkColor placeholder:text-zinc-400 border border-transparent focus:border-themeColor outline-none transition-colors"
                          />

                          {customPropertyCount > 0 && (
                            <div className="mt-2">
                              {customPricing?.discountPercentage > 0 && (
                                <ParagraphTag
                                  content={`${customPricing.discountPercentage}% volume discount — saving ${customPricing.discountAmount}`}
                                  classes="text-statusColor font-popinsMedium text-xs group-hover:text-green-200 transition-colors duration-300"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Pricing */}
                      <div className="mt-4">
                        <ParagraphTag
                          content={
                            isCustom
                              ? customPricing?.totalPrice || "$0"
                              : displayPackage.totalPrice
                          }
                          classes="text-darkColor font-popinsBold text-3xl group-hover:text-white transition-colors duration-300"
                        />
                        {isCustom && customPricing?.discountPercentage > 0 && (
                          <ParagraphTag
                            content={`List price: ${customPricing.originalPrice}`}
                            classes="text-paraColor font-popinsRegular text-xs line-through group-hover:text-white/50 transition-colors duration-300"
                          />
                        )}
                        <ParagraphTag
                          content={`${
                            isCustom
                              ? customPricing?.monthlyPrice || "$0"
                              : displayPackage.monthlyPrice
                          } / month`}
                          classes="text-paraColor font-popinsRegular text-sm mt-0.5 group-hover:text-white/70 transition-colors duration-300"
                        />
                        {displayPackage.savePerYear && !isCustom && (
                          <ParagraphTag
                            content={`Save ${displayPackage.savePerYear} vs. monthly`}
                            classes="text-statusColor font-popinsMedium text-xs mt-1 group-hover:text-green-200 transition-colors duration-300"
                          />
                        )}
                        {displayPackage.description && (
                          <ParagraphTag
                            content={displayPackage.description}
                            classes="text-paraColor/70 pt-2 font-popinsRegular text-xs group-hover:text-white/60 transition-colors duration-300 leading-relaxed"
                          />
                        )}
                      </div>
                    </div>

                    {/* Features list */}
                    <div className="flex-1 pt-6 pb-4">
                      {displayPackage.features.map((feature, j) => (
                        <div key={j} className="mt-3 flex items-start">
                          <div className="w-4 h-4 flex-shrink-0 flex justify-center items-center rounded-full bg-themeColor group-hover:bg-white transition-colors duration-300 mt-0.5">
                            <Check
                              size={10}
                              className="text-white group-hover:text-themeColor transition-colors duration-300"
                            />
                          </div>
                          <ParagraphTag
                            content={feature}
                            classes="text-darkColor pl-2.5 font-popinsRegular text-xs group-hover:text-white/90 transition-colors duration-300 leading-relaxed"
                          />
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <div className="pb-7 mt-2">
                      <Link to="/request-demo">
                        <button className="w-full py-3 rounded-xl font-popinsSemiBold text-sm bg-themeColor text-white group-hover:bg-white group-hover:text-themeColor border border-themeColor group-hover:border-white transition-all duration-300 hover:shadow-md">
                          Get Started
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Bottom trust line */}
        <p className="text-center font-popinsRegular text-xs text-paraColor mt-4">
          Questions about which plan fits your portfolio?{" "}
          <Link to="/contact-us" className="text-themeColor font-popinsMedium underline underline-offset-2">
            Talk to our team
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPlan;

export const ParagraphTag = ({ content, classes, onTrigger }) => (
  <h1 className={classes} onClick={onTrigger}>
    {content}
  </h1>
);
